import { useState, useCallback, useMemo } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    demo?: 'drag' | 'resize' | 'hide' | 'contribute' // Optional demo animation type
}

interface DepartmentTourConfig {
    id: string
    name: string
    description: string
    steps: TourStep[]
}

/**
 * Widget tour configurations for each department
 * Maps widget IDs to their descriptions
 */
const WIDGET_DESCRIPTIONS: Record<
    string,
    { title: string; description: string }
> = {
    // Common widgets
    quote_of_the_day: {
        title: 'Câu nói trong ngày',
        description:
            'Hiển thị câu nói truyền cảm hứng mỗi ngày.\n• Refresh để xem quote khác\n• Translate để dịch sang tiếng Việt',
    },
    'quote-contribute': {
        title: 'Đóng góp Quote',
        description:
            'Chia sẻ câu nói hay với đồng nghiệp.\n• Click nút + để mở form\n• Nhập nội dung và tác giả\n• Quote được duyệt sẽ hiển thị ngẫu nhiên',
    },
    revenue: {
        title: 'Doanh thu mục tiêu',
        description:
            'Tổng quan doanh thu công ty.\n• So sánh thực tế vs mục tiêu\n• Biểu đồ tiến độ trực quan',
    },
    'revenue-time-selector': {
        title: 'Chọn khoảng thời gian',
        description:
            'Lọc dữ liệu theo tháng/quý.\n• Mặc định: Quý hiện tại\n• Click để chọn khoảng thời gian khác\n• Dữ liệu tự động cập nhật',
    },
    'revenue-actual': {
        title: 'Doanh thu thực tế',
        description:
            'Tổng doanh thu đã đạt được.\n• Số liệu từ hóa đơn đã thanh toán\n• Màu xanh lá = đạt/vượt mục tiêu',
    },
    'revenue-target': {
        title: 'Mục tiêu công ty',
        description:
            'Target doanh thu cần đạt.\n• Được thiết lập theo quý/năm\n• Dùng để đánh giá hiệu quả kinh doanh',
    },
    'revenue-completion': {
        title: 'Tỷ lệ hoàn thành',
        description:
            'Phần trăm hoàn thành mục tiêu.\n• = (Thực tế / Mục tiêu)  100%\n• ≥100% = Vượt target',
    },
    notification: {
        title: 'Thông báo',
        description:
            'Danh sách thông báo từ hệ thống.\n• Công việc được giao\n• Cập nhật ticket\n• Nhắc nhở deadline',
    },
    workday: {
        title: 'Lịch làm việc thứ 7',
        description:
            'Lịch làm việc thứ 7 và ngày lễ trong tháng.\n• Các ngày thứ 7 làm việc\n• Ngày lễ, ngày nghỉ bù\n• Ngày remote toàn công ty',
    },
    'workday-add-holiday': {
        title: 'Thêm ngày lễ/Remote',
        description:
            'Thêm ngày lễ hoặc ngày remote.\n• Click để mở form tạo\n• Chọn loại: Ngày lễ / Remote\n• Áp dụng cho toàn công ty',
    },
    my_workdays: {
        title: 'Chấm công của tôi',
        description:
            'Chi tiết chấm công cá nhân.\n• Giờ vào/ra mỗi ngày\n• Tổng giờ làm việc\n• Trạng thái chấm công',
    },
    events: {
        title: 'Sự kiện',
        description:
            'Lịch sự kiện công ty.\n• Sinh nhật nhân viên\n• Họp định kỳ\n• Sự kiện team building',
    },
    business: {
        title: 'Công tác',
        description:
            'Danh sách đề xuất công tác.\n• Lịch công tác\n• Trạng thái phê duyệt\n• Chi phí dự kiến',
    },

    // Widget Store (common for all)
    'widget-store': {
        title: 'Kho Widget',
        description:
            'Thêm widget vào dashboard.\n• Click để mở kho widget\n• Kéo thả widget vào dashboard\n• Badge hiển thị số widget khả dụng',
    },

    // Dashboard controls (common for all)
    'dashboard-lock': {
        title: 'Khóa/Mở khóa Dashboard',
        description:
            'Kiểm soát chế độ chỉnh sửa.\n• Mở khóa: Có thể kéo thả, resize\n• Khóa: Cố định vị trí widget\n• Khuyến nghị khóa sau khi sắp xếp xong',
    },
    'widget-reorder': {
        title: 'Sắp xếp Widget',
        description:
            'Thay đổi vị trí widget.\n• Mở khóa dashboard trước\n• Kéo widget đến vị trí mong muốn\n• Thả để xác nhận vị trí mới',
    },
    'widget-resize': {
        title: 'Thay đổi kích thước',
        description:
            'Resize widget theo ý muốn.\n• Hover vào widget → góc dưới phải\n• Kéo handle để resize\n• Hiển thị tỷ lệ (VD: 6/12)',
    },
    'widget-hide': {
        title: 'Ẩn Widget',
        description:
            'Ẩn widget không cần thiết.\n• Mở khóa dashboard\n• Kéo widget xuống dưới\n• Thả vào vùng "Kéo vào đây để ẩn"',
    },

    // Sales specific
    'daily-input': {
        title: 'Input hàng ngày',
        description:
            'Theo dõi hoạt động sales hàng ngày.\n• Số cuộc gọi\n• Số email gửi\n• Số meeting đã book\n• Tổng hợp theo tuần/tháng',
    },
    'conversion-funnel': {
        title: 'Phễu chuyển đổi',
        description:
            'Biểu đồ phễu chuyển đổi khách hàng.\n• Lead → Opportunity → Quote → Close\n• Tỷ lệ chuyển đổi từng giai đoạn\n• So sánh với kỳ trước',
    },
    'funnel-summary-stats': {
        title: 'Thống kê tổng quan',
        description:
            'Các chỉ số quan trọng của phễu.\n• So sánh với kỳ trước (tăng/giảm)\n• Click để xem chi tiết từng giai đoạn',
    },
    'funnel-contacts': {
        title: 'Contacts',
        description:
            'Số khách hàng mới nhập vào hệ thống.\n• Trong khoảng thời gian đã chọn\n• Nguồn: Input thủ công, Import, Web form\n• Là điểm đầu của phễu chuyển đổi',
    },
    'funnel-presentation': {
        title: 'Presentation',
        description:
            'Số buổi trình bày/gặp khách hàng.\n• Phiếu công tác gặp KH được tạo\n• Buổi demo sản phẩm/dịch vụ\n• Tỷ lệ = Presentation / Contacts',
    },
    'funnel-poc': {
        title: 'POC (Proof of Concept)',
        description:
            'Số POC đang triển khai.\n• Khách hàng dùng thử dịch vụ\n• Giai đoạn đánh giá trước ký HĐ\n• Thời hạn POC thường 7-30 ngày',
    },
    'funnel-close-win': {
        title: 'Close Win',
        description:
            'Số dịch vụ được mở thành công.\n• Khách đã ký hợp đồng\n• Dịch vụ đã được kích hoạt\n• Mục tiêu cuối cùng của Sales',
    },
    'funnel-close-lose': {
        title: 'Close Lose',
        description:
            'Số cơ hội bị mất.\n• Khách từ chối sau POC/Presentation\n• Lý do: Giá, đối thủ, không phù hợp\n• Cần phân tích để cải thiện',
    },
    'leads-statistics': {
        title: 'Thống kê Leads',
        description:
            'Contacts từ Marketing đổ về.\n• Leads từ Website, Landing page, Campaign\n• Phân loại theo trạng thái xử lý\n• 30 ngày gần nhất',
    },
    'leads-total': {
        title: 'Tổng số Leads',
        description:
            'Tổng số contacts Marketing đổ về.\n• Tất cả nguồn: Web, Landing, Ads\n• Bao gồm mọi trạng thái\n• Trong 30 ngày gần nhất',
    },
    'leads-unverified': {
        title: 'Chưa xác minh',
        description:
            'Leads chưa được update/care.\n• Mới đổ về, chưa liên hệ\n• Cần Sales verify thông tin\n• Ưu tiên xử lý sớm',
    },
    'leads-following': {
        title: 'Đang theo dõi',
        description:
            'Leads đang được Sales chăm sóc.\n• Đã liên hệ, đang tư vấn\n• Có tiềm năng chuyển đổi\n• Cần follow-up định kỳ',
    },
    'leads-matched': {
        title: 'Đã khớp',
        description:
            'Leads đã match với customer.\n• Thông tin trùng khớp trong hệ thống\n• Đã có lịch sử giao dịch\n• Không cần tạo KH mới',
    },
    'leads-un_matched': {
        title: 'Không khớp',
        description:
            'Leads không match được.\n• Thông tin không đủ/sai\n• Không tìm thấy trong hệ thống\n• Cần xác minh thêm',
    },
    'leads-spam': {
        title: 'Spam',
        description:
            'Leads spam/không hợp lệ.\n• Email/SĐT giả, test\n• Đối thủ, quảng cáo\n• Loại khỏi pipeline',
    },
    alerts: {
        title: 'Cảnh báo Sales',
        description:
            'Thông báo quan trọng cho sales.\n• Hợp đồng sắp hết hạn\n• Khách hàng chưa liên hệ lâu\n• POC sắp hết hạn',
    },
    'sla-customers': {
        title: 'Khách hàng SLA',
        description:
            'Danh sách khách hàng có SLA.\n• Thời gian uptime cam kết\n• Ticket đang mở\n• Mức độ ưu tiên',
    },
    'poc-expiring': {
        title: 'POC sắp hết hạn',
        description:
            'Danh sách POC cần theo dõi.\n• POC sắp hết hạn trong 7/14/30 ngày\n• Trạng thái đánh giá\n• Action cần thực hiện',
    },

    // Account specific
    'cancellation-rate': {
        title: 'Tỷ lệ hủy dịch vụ',
        description:
            'Biểu đồ tỷ lệ hủy/giữ chân KH.\n• Cancellation rate vs Retention rate\n• So sánh với năm trước\n• Phân tích xu hướng',
    },
    'cancellation-rate-avg': {
        title: 'Tỷ lệ hủy trung bình',
        description:
            'Tỷ lệ hủy TB trong năm.\n• Màu đỏ = Tỷ lệ hủy\n• Giảm = Tốt (màu xanh)\n• Tăng = Cần cải thiện (màu đỏ)',
    },
    'cancellation-analysis': {
        title: 'Phân tích chi tiết',
        description:
            'Insights về tỷ lệ hủy:\n• Retention rate TB\n• Tháng hủy cao/thấp nhất\n• Xu hướng (Improving/Worsening)',
    },
    'invoice-waiting': {
        title: 'Hóa đơn',
        description:
            'Quản lý hóa đơn chờ xử lý.\n• Tab Chờ xác nhận: HĐ cần duyệt\n• Tab Công nợ: HĐ đã duyệt chưa TT\n• Click để xem chi tiết',
    },
    'invoice-tabs': {
        title: 'Loại hóa đơn',
        description:
            'Chuyển đổi giữa 2 loại:\n• Chờ xác nhận: Cần Account xác nhận\n• Công nợ: KH chưa thanh toán',
    },
    'invoice-waiting-summary': {
        title: 'Tổng chờ xác nhận',
        description:
            'Số HĐ cần xác nhận.\n• Được tạo từ đơn hàng\n• Account review & duyệt\n• Sau duyệt → Gửi KH',
    },
    'invoice-debt-summary': {
        title: 'Tổng công nợ',
        description:
            'Số HĐ công nợ.\n• Đã gửi KH, chưa thanh toán\n• Cần theo dõi, nhắc nợ\n• Ảnh hưởng dòng tiền',
    },
    'service-trend': {
        title: 'Xu hướng dịch vụ',
        description:
            'Biểu đồ dịch vụ theo tháng.\n• Số DV mới mở (xanh lá)\n• Số DV hủy (đỏ)\n• Tổng DV cuối kỳ (line)',
    },
    'service-trend-analysis': {
        title: 'Phân tích xu hướng',
        description:
            'Insights dịch vụ:\n• Net change (Tổng tăng/giảm)\n• Tháng mở DV nhiều nhất\n• Tháng hủy DV nhiều nhất',
    },
    'waiting-confirm': {
        title: 'Chờ xác nhận',
        description:
            'DV/POC chờ Account xác nhận.\n• Tab Dịch vụ: DV mới đăng ký\n• Tab POC: POC cần confirm\n• Click để xem chi tiết',
    },
    'waiting-confirm-tabs': {
        title: 'Loại chờ xác nhận',
        description:
            'Chuyển đổi giữa 2 loại:\n• Dịch vụ: DV mới từ Sales\n• POC: POC kết thúc cần confirm',
    },
    'waiting-service-summary': {
        title: 'Tổng DV chờ',
        description:
            'Số DV chờ xác nhận.\n• Sales tạo đơn → Chờ Account\n• Kiểm tra thông tin KH, gói DV\n• Confirm → Kích hoạt DV',
    },
    'waiting-poc-summary': {
        title: 'Tổng POC chờ',
        description:
            'Số POC chờ xác nhận.\n• POC hết hạn → Chờ confirm\n• Confirm → Chuyển DV chính thức\n• Hoặc extend/hủy POC',
    },

    // Finance specific
    invoicable: {
        title: 'Có thể xuất hóa đơn',
        description:
            'HĐ đã duyệt, cần xuất VAT.\n• Đã confirm revenue\n• Chưa xuất HĐ VAT\n• Click để xuất HĐ',
    },
    'invoicable-summary': {
        title: 'Tổng cần xuất HĐ',
        description:
            'Số HĐ chờ xuất VAT.\n• Account đã confirm\n• Finance cần xuất HĐ VAT\n• Gửi KH để thu tiền',
    },
    'revenue-allocation': {
        title: 'Phân bổ doanh thu',
        description:
            'Tổng quan DT trong quý.\n• DT thực tế vs Dự kiến\n• Theo trạng thái thanh toán\n• Số bản ghi đã/chưa TT',
    },
    'revenue-alloc-total': {
        title: 'Tổng doanh thu',
        description:
            'Tổng DT phân bổ trong quý.\n• Bao gồm cả thực tế và dự kiến\n• Đơn vị: VND\n• Cập nhật realtime',
    },
    'revenue-alloc-by-type': {
        title: 'Theo loại doanh thu',
        description:
            'Biểu đồ DT theo loại:\n• Xanh lá = Thực tế (đã confirm)\n• Cam = Dự kiến (chưa confirm)\n• Hover để xem số liệu',
    },
    'revenue-alloc-by-payment': {
        title: 'Theo thanh toán',
        description:
            'Biểu đồ theo trạng thái TT:\n• Xanh = Đã thanh toán\n• Đỏ = Chưa thanh toán\n• Ảnh hưởng dòng tiền',
    },
    'revenue-alloc-stats': {
        title: 'Số bản ghi',
        description:
            'Đếm số bản ghi phân bổ:\n• Đã TT: Bản ghi KH đã thanh toán\n• Chưa TT: Bản ghi công nợ\n• Click để xem chi tiết',
    },
    'contract-alerts': {
        title: 'Cảnh báo hợp đồng',
        description:
            'HĐ cần gia hạn/xử lý.\n• Đã hết hạn (màu đỏ)\n• Sắp hết hạn (màu cam)\n• Click để xem HĐ',
    },
    'contract-expired': {
        title: 'Đã hết hạn',
        description:
            'HĐ đã quá hạn.\n• Cần liên hệ KH gia hạn\n• Số ngày quá hạn hiển thị\n• Ưu tiên xử lý ngay',
    },
    'contract-expiring': {
        title: 'Sắp hết hạn',
        description:
            'HĐ sắp hết hạn.\n• Trong vòng 30 ngày\n• Số ngày còn lại hiển thị\n• Chủ động liên hệ KH',
    },
    'recently-paid': {
        title: 'Thanh toán gần đây',
        description:
            'HĐ vừa được thanh toán.\n• Danh sách HĐ đã TT\n• Số tiền và ngày TT\n• Theo dõi dòng tiền vào',
    },
    'recently-paid-summary': {
        title: 'Tổng đã thanh toán',
        description:
            'Thống kê HĐ đã TT:\n• Số lượng HĐ\n• Tổng giá trị top 5\n• Click xem chi tiết',
    },

    // HR specific
    'interview-schedule': {
        title: 'Lịch phỏng vấn',
        description:
            'Lịch PV ứng viên tuần này/sau.\n• Thông tin ứng viên\n• Vị trí, phòng ban\n• Người phỏng vấn',
    },
    'interview-count': {
        title: 'Số ứng viên',
        description:
            'Tổng UV có lịch PV.\n• Tuần này + tuần sau\n• Badge hiển thị số lượng',
    },
    'interview-this-week': {
        title: 'Tuần này',
        description:
            'UV phỏng vấn tuần này.\n• Hôm nay (xanh lá), Ngày mai (vàng)\n• Thông tin liên hệ UV\n• Click để xem chi tiết',
    },
    'interview-next-week': {
        title: 'Tuần sau',
        description:
            'UV phỏng vấn tuần sau.\n• Lên kế hoạch trước\n• Chuẩn bị câu hỏi PV\n• Sắp xếp người PV',
    },
    'late-employees': {
        title: 'Nhân viên đi muộn',
        description:
            'Thống kê đi muộn theo ngày.\n• Chọn ngày trên calendar\n• Group theo phòng ban\n• Hiển thị phút đi muộn',
    },
    'late-employees-stats': {
        title: 'Thống kê',
        description:
            'Tổng số người đi muộn:\n• Badge đỏ = Số người\n• Badge cam = Tổng phút muộn\n• Cập nhật theo ngày chọn',
    },
    'late-employees-calendar': {
        title: 'Chọn ngày',
        description:
            'Calendar chọn ngày xem:\n• Mặc định hôm qua\n• Không chọn được CN\n• Chuyển tuần bằng mũi tên',
    },

    // Tech specific
    tickets: {
        title: 'Ticket Của Tôi',
        description:
            'Tổng quan ticket được phân công.\n• Biểu đồ tròn theo trạng thái\n• Chuyển đổi giữa Biểu đồ/Danh sách',
    },
    'ticket-chart-legend': {
        title: 'Phân loại Ticket',
        description:
            'Các trạng thái ticket:\n• Chưa phân công: Ticket chưa có Tech\n• Đang xử lý: Ticket đang được xử lý\n• Đã hoàn thành: Tech đã xử lý xong\n• Đã đóng: Leader đã confirm đóng',
    },
    'ticket-alert-overdue': {
        title: 'Ticket Quá Hạn',
        description:
            'Số ticket đã quá deadline.\n• Cần ưu tiên xử lý ngay\n• Click để xem danh sách chi tiết',
    },
    'ticket-alert-high-priority': {
        title: 'Ticket Ưu Tiên Cao',
        description:
            'Số ticket có mức ưu tiên cao.\n• Cần xử lý trước các ticket khác\n• Thường là ticket ảnh hưởng nhiều user',
    },
    'oncall-timeline': {
        title: 'Lịch trực Oncall',
        description:
            'Timeline trực oncall.\n• Người trực hôm nay\n• Lịch trực tuần\n• Số incident đã xử lý',
    },
    'sla-dashboard': {
        title: 'Dashboard SLA',
        description:
            'Tổng quan SLA.\n• Uptime theo tháng\n• Số incident\n• Thời gian downtime',
    },
}

/**
 * Common intro steps for all department tours (dashboard controls)
 */
const COMMON_INTRO_STEPS: TourStep[] = [
    {
        selector: '[data-tour="dashboard-lock-button"]',
        ...WIDGET_DESCRIPTIONS['dashboard-lock'],
    },
    {
        // Target first widget for reorder instruction
        selector: '[data-tour^="widget-"]',
        ...WIDGET_DESCRIPTIONS['widget-reorder'],
        demo: 'drag',
    },
    {
        // Target first widget for resize instruction
        selector: '[data-tour^="widget-"]',
        ...WIDGET_DESCRIPTIONS['widget-resize'],
        demo: 'resize',
    },
    {
        // Target first widget for hide instruction
        selector: '[data-tour^="widget-"]',
        ...WIDGET_DESCRIPTIONS['widget-hide'],
        demo: 'hide',
    },
]

/**
 * Quote contribute step (shown after quote_of_the_day widget)
 */
const QUOTE_CONTRIBUTE_STEP: TourStep = {
    selector: '[data-tour="quote-contribute-button"]',
    ...WIDGET_DESCRIPTIONS['quote-contribute'],
    demo: 'contribute',
}

/**
 * Common final step for all department tours
 */
const COMMON_FINAL_STEPS: TourStep[] = [
    {
        selector: '[data-tour="widget-store-button"]',
        ...WIDGET_DESCRIPTIONS['widget-store'],
    },
]

/**
 * Department tour configurations
 */
const DEPARTMENT_TOURS: Record<string, DepartmentTourConfig> = {
    SALES: {
        id: 'sales-dashboard',
        name: 'Dashboard Sales',
        description: 'Hướng dẫn các widget trên dashboard phòng Sales',
        steps: [
            {
                selector: '[data-tour="widget-quote_of_the_day"]',
                ...WIDGET_DESCRIPTIONS.quote_of_the_day,
            },
            {
                selector: '[data-tour="widget-revenue"]',
                ...WIDGET_DESCRIPTIONS.revenue,
            },
            // Revenue detail sub-steps
            {
                selector: '[data-tour="revenue-time-selector"]',
                ...WIDGET_DESCRIPTIONS['revenue-time-selector'],
            },
            {
                selector: '[data-tour="revenue-actual"]',
                ...WIDGET_DESCRIPTIONS['revenue-actual'],
            },
            {
                selector: '[data-tour="revenue-target"]',
                ...WIDGET_DESCRIPTIONS['revenue-target'],
            },
            {
                selector: '[data-tour="revenue-completion"]',
                ...WIDGET_DESCRIPTIONS['revenue-completion'],
            },
            {
                selector: '[data-tour="widget-daily-input"]',
                ...WIDGET_DESCRIPTIONS['daily-input'],
            },
            {
                selector: '[data-tour="widget-conversion-funnel"]',
                ...WIDGET_DESCRIPTIONS['conversion-funnel'],
            },
            // Funnel detail sub-steps
            {
                selector: '[data-tour="funnel-contacts"]',
                ...WIDGET_DESCRIPTIONS['funnel-contacts'],
            },
            {
                selector: '[data-tour="funnel-presentation"]',
                ...WIDGET_DESCRIPTIONS['funnel-presentation'],
            },
            {
                selector: '[data-tour="funnel-poc"]',
                ...WIDGET_DESCRIPTIONS['funnel-poc'],
            },
            {
                selector: '[data-tour="funnel-close-win"]',
                ...WIDGET_DESCRIPTIONS['funnel-close-win'],
            },
            {
                selector: '[data-tour="funnel-close-lose"]',
                ...WIDGET_DESCRIPTIONS['funnel-close-lose'],
            },
            {
                selector: '[data-tour="widget-leads-statistics"]',
                ...WIDGET_DESCRIPTIONS['leads-statistics'],
            },
            // Leads detail sub-steps
            {
                selector: '[data-tour="leads-total"]',
                ...WIDGET_DESCRIPTIONS['leads-total'],
            },
            {
                selector: '[data-tour="leads-unverified"]',
                ...WIDGET_DESCRIPTIONS['leads-unverified'],
            },
            {
                selector: '[data-tour="leads-following"]',
                ...WIDGET_DESCRIPTIONS['leads-following'],
            },
            {
                selector: '[data-tour="leads-matched"]',
                ...WIDGET_DESCRIPTIONS['leads-matched'],
            },
            {
                selector: '[data-tour="leads-un_matched"]',
                ...WIDGET_DESCRIPTIONS['leads-un_matched'],
            },
            {
                selector: '[data-tour="leads-spam"]',
                ...WIDGET_DESCRIPTIONS['leads-spam'],
            },
            {
                selector: '[data-tour="widget-alerts"]',
                ...WIDGET_DESCRIPTIONS.alerts,
            },
            {
                selector: '[data-tour="widget-business"]',
                ...WIDGET_DESCRIPTIONS.business,
            },
            {
                selector: '[data-tour="widget-my_workdays"]',
                ...WIDGET_DESCRIPTIONS.my_workdays,
            },
            {
                selector: '[data-tour="widget-notification"]',
                ...WIDGET_DESCRIPTIONS.notification,
            },
            {
                selector: '[data-tour="widget-workday"]',
                ...WIDGET_DESCRIPTIONS.workday,
            },
            {
                selector: '[data-tour="widget-events"]',
                ...WIDGET_DESCRIPTIONS.events,
            },
            {
                selector: '[data-tour="widget-sla-customers"]',
                ...WIDGET_DESCRIPTIONS['sla-customers'],
            },
        ],
    },
    // SALE PARTNER uses same dashboard as SALES
    'SALE PARTNER': {
        id: 'sale-partner-dashboard',
        name: 'Dashboard Sale Partner',
        description: 'Hướng dẫn các widget trên dashboard phòng Sale Partner',
        steps: [], // Will be populated from SALES
    },
    ACCOUNT: {
        id: 'account-dashboard',
        name: 'Dashboard Account',
        description: 'Hướng dẫn các widget trên dashboard phòng Account',
        steps: [
            {
                selector: '[data-tour="widget-quote_of_the_day"]',
                ...WIDGET_DESCRIPTIONS.quote_of_the_day,
            },
            {
                selector: '[data-tour="widget-cancellation-rate"]',
                ...WIDGET_DESCRIPTIONS['cancellation-rate'],
            },
            // Cancellation rate sub-steps
            {
                selector: '[data-tour="cancellation-rate-avg"]',
                ...WIDGET_DESCRIPTIONS['cancellation-rate-avg'],
            },
            {
                selector: '[data-tour="cancellation-analysis"]',
                ...WIDGET_DESCRIPTIONS['cancellation-analysis'],
            },
            {
                selector: '[data-tour="widget-invoice-waiting"]',
                ...WIDGET_DESCRIPTIONS['invoice-waiting'],
            },
            // Invoice sub-steps
            {
                selector: '[data-tour="invoice-tabs"]',
                ...WIDGET_DESCRIPTIONS['invoice-tabs'],
            },
            {
                selector: '[data-tour="invoice-waiting-summary"]',
                ...WIDGET_DESCRIPTIONS['invoice-waiting-summary'],
            },
            {
                selector: '[data-tour="widget-service-trend"]',
                ...WIDGET_DESCRIPTIONS['service-trend'],
            },
            // Service trend sub-steps
            {
                selector: '[data-tour="service-trend-analysis"]',
                ...WIDGET_DESCRIPTIONS['service-trend-analysis'],
            },
            {
                selector: '[data-tour="widget-waiting-confirm"]',
                ...WIDGET_DESCRIPTIONS['waiting-confirm'],
            },
            // Waiting confirm sub-steps
            {
                selector: '[data-tour="waiting-confirm-tabs"]',
                ...WIDGET_DESCRIPTIONS['waiting-confirm-tabs'],
            },
            {
                selector: '[data-tour="waiting-service-summary"]',
                ...WIDGET_DESCRIPTIONS['waiting-service-summary'],
            },
            {
                selector: '[data-tour="widget-my_workdays"]',
                ...WIDGET_DESCRIPTIONS.my_workdays,
            },
            {
                selector: '[data-tour="widget-notification"]',
                ...WIDGET_DESCRIPTIONS.notification,
            },
            {
                selector: '[data-tour="widget-workday"]',
                ...WIDGET_DESCRIPTIONS.workday,
            },
        ],
    },
    FINANCE: {
        id: 'finance-dashboard',
        name: 'Dashboard Finance',
        description: 'Hướng dẫn các widget trên dashboard phòng Finance',
        steps: [
            {
                selector: '[data-tour="widget-quote_of_the_day"]',
                ...WIDGET_DESCRIPTIONS.quote_of_the_day,
            },
            {
                selector: '[data-tour="widget-invoicable"]',
                ...WIDGET_DESCRIPTIONS.invoicable,
            },
            // Invoicable sub-step
            {
                selector: '[data-tour="invoicable-summary"]',
                ...WIDGET_DESCRIPTIONS['invoicable-summary'],
            },
            {
                selector: '[data-tour="widget-revenue-allocation"]',
                ...WIDGET_DESCRIPTIONS['revenue-allocation'],
            },
            // Revenue allocation sub-steps
            {
                selector: '[data-tour="revenue-alloc-total"]',
                ...WIDGET_DESCRIPTIONS['revenue-alloc-total'],
            },
            {
                selector: '[data-tour="revenue-alloc-by-type"]',
                ...WIDGET_DESCRIPTIONS['revenue-alloc-by-type'],
            },
            {
                selector: '[data-tour="revenue-alloc-by-payment"]',
                ...WIDGET_DESCRIPTIONS['revenue-alloc-by-payment'],
            },
            {
                selector: '[data-tour="revenue-alloc-stats"]',
                ...WIDGET_DESCRIPTIONS['revenue-alloc-stats'],
            },
            {
                selector: '[data-tour="widget-contract-alerts"]',
                ...WIDGET_DESCRIPTIONS['contract-alerts'],
            },
            // Contract alerts sub-steps
            {
                selector: '[data-tour="contract-expired"]',
                ...WIDGET_DESCRIPTIONS['contract-expired'],
            },
            {
                selector: '[data-tour="contract-expiring"]',
                ...WIDGET_DESCRIPTIONS['contract-expiring'],
            },
            {
                selector: '[data-tour="widget-recently-paid"]',
                ...WIDGET_DESCRIPTIONS['recently-paid'],
            },
            // Recently paid sub-step
            {
                selector: '[data-tour="recently-paid-summary"]',
                ...WIDGET_DESCRIPTIONS['recently-paid-summary'],
            },
            {
                selector: '[data-tour="widget-my_workdays"]',
                ...WIDGET_DESCRIPTIONS.my_workdays,
            },
            {
                selector: '[data-tour="widget-notification"]',
                ...WIDGET_DESCRIPTIONS.notification,
            },
            {
                selector: '[data-tour="widget-workday"]',
                ...WIDGET_DESCRIPTIONS.workday,
            },
        ],
    },
    'HUMAN RESOURCE': {
        id: 'hr-dashboard',
        name: 'Dashboard HR',
        description: 'Hướng dẫn các widget trên dashboard phòng HR',
        steps: [
            {
                selector: '[data-tour="widget-quote_of_the_day"]',
                ...WIDGET_DESCRIPTIONS.quote_of_the_day,
            },
            {
                selector: '[data-tour="widget-interview-schedule"]',
                ...WIDGET_DESCRIPTIONS['interview-schedule'],
            },
            // Interview schedule sub-steps
            {
                selector: '[data-tour="interview-count"]',
                ...WIDGET_DESCRIPTIONS['interview-count'],
            },
            {
                selector: '[data-tour="interview-this-week"]',
                ...WIDGET_DESCRIPTIONS['interview-this-week'],
            },
            {
                selector: '[data-tour="interview-next-week"]',
                ...WIDGET_DESCRIPTIONS['interview-next-week'],
            },
            {
                selector: '[data-tour="widget-late-employees"]',
                ...WIDGET_DESCRIPTIONS['late-employees'],
            },
            // Late employees sub-steps
            {
                selector: '[data-tour="late-employees-stats"]',
                ...WIDGET_DESCRIPTIONS['late-employees-stats'],
            },
            {
                selector: '[data-tour="late-employees-calendar"]',
                ...WIDGET_DESCRIPTIONS['late-employees-calendar'],
            },
            {
                selector: '[data-tour="widget-my_workdays"]',
                ...WIDGET_DESCRIPTIONS.my_workdays,
            },
            {
                selector: '[data-tour="widget-workday"]',
                ...WIDGET_DESCRIPTIONS.workday,
            },
            // HR-specific: Add holiday/remote action
            {
                selector: '[data-tour="workday-add-holiday"]',
                ...WIDGET_DESCRIPTIONS['workday-add-holiday'],
            },
            {
                selector: '[data-tour="widget-events"]',
                ...WIDGET_DESCRIPTIONS.events,
            },
            {
                selector: '[data-tour="widget-notification"]',
                ...WIDGET_DESCRIPTIONS.notification,
            },
        ],
    },
    TECHNOLOGY: {
        id: 'tech-dashboard',
        name: 'Dashboard Technology',
        description: 'Hướng dẫn các widget trên dashboard phòng Technology',
        steps: [
            {
                selector: '[data-tour="widget-quote_of_the_day"]',
                ...WIDGET_DESCRIPTIONS.quote_of_the_day,
            },
            {
                selector: '[data-tour="widget-tickets"]',
                ...WIDGET_DESCRIPTIONS.tickets,
            },
            // Ticket detail sub-steps
            {
                selector: '[data-tour="ticket-chart-legend"]',
                ...WIDGET_DESCRIPTIONS['ticket-chart-legend'],
            },
            {
                selector: '[data-tour="ticket-alert-overdue"]',
                ...WIDGET_DESCRIPTIONS['ticket-alert-overdue'],
            },
            {
                selector: '[data-tour="ticket-alert-high-priority"]',
                ...WIDGET_DESCRIPTIONS['ticket-alert-high-priority'],
            },
            {
                selector: '[data-tour="widget-oncall-timeline"]',
                ...WIDGET_DESCRIPTIONS['oncall-timeline'],
            },
            {
                selector: '[data-tour="widget-sla-dashboard"]',
                ...WIDGET_DESCRIPTIONS['sla-dashboard'],
            },
            {
                selector: '[data-tour="widget-my_workdays"]',
                ...WIDGET_DESCRIPTIONS.my_workdays,
            },
            {
                selector: '[data-tour="widget-notification"]',
                ...WIDGET_DESCRIPTIONS.notification,
            },
            {
                selector: '[data-tour="widget-workday"]',
                ...WIDGET_DESCRIPTIONS.workday,
            },
        ],
    },
    // DEVELOPMENT uses same dashboard as TECHNOLOGY
    DEVELOPMENT: {
        id: 'development-dashboard',
        name: 'Dashboard Development',
        description: 'Hướng dẫn các widget trên dashboard phòng Development',
        steps: [], // Will be populated from TECHNOLOGY
    },
    MARKETING: {
        id: 'marketing-dashboard',
        name: 'Dashboard Marketing',
        description: 'Hướng dẫn các widget trên dashboard phòng Marketing',
        steps: [
            {
                selector: '[data-tour="widget-quote_of_the_day"]',
                ...WIDGET_DESCRIPTIONS.quote_of_the_day,
            },
            {
                selector: '[data-tour="widget-leads-statistics"]',
                ...WIDGET_DESCRIPTIONS['leads-statistics'],
            },
            // Leads detail sub-steps
            {
                selector: '[data-tour="leads-total"]',
                ...WIDGET_DESCRIPTIONS['leads-total'],
            },
            {
                selector: '[data-tour="leads-unverified"]',
                ...WIDGET_DESCRIPTIONS['leads-unverified'],
            },
            {
                selector: '[data-tour="leads-following"]',
                ...WIDGET_DESCRIPTIONS['leads-following'],
            },
            {
                selector: '[data-tour="leads-matched"]',
                ...WIDGET_DESCRIPTIONS['leads-matched'],
            },
            {
                selector: '[data-tour="leads-un_matched"]',
                ...WIDGET_DESCRIPTIONS['leads-un_matched'],
            },
            {
                selector: '[data-tour="leads-spam"]',
                ...WIDGET_DESCRIPTIONS['leads-spam'],
            },
            {
                selector: '[data-tour="widget-my_workdays"]',
                ...WIDGET_DESCRIPTIONS.my_workdays,
            },
            {
                selector: '[data-tour="widget-notification"]',
                ...WIDGET_DESCRIPTIONS.notification,
            },
            {
                selector: '[data-tour="widget-workday"]',
                ...WIDGET_DESCRIPTIONS.workday,
            },
            {
                selector: '[data-tour="widget-events"]',
                ...WIDGET_DESCRIPTIONS.events,
            },
        ],
    },
    // Board of Directors / Executive Dashboard
    'BOARD OF DIRECTORS': {
        id: 'bod-dashboard',
        name: 'Dashboard Ban Giám Đốc',
        description: 'Hướng dẫn các widget trên dashboard BGĐ',
        steps: [
            {
                selector: '[data-tour="widget-quote_of_the_day"]',
                ...WIDGET_DESCRIPTIONS.quote_of_the_day,
            },
            {
                selector: '[data-tour="widget-revenue"]',
                ...WIDGET_DESCRIPTIONS.revenue,
            },
            // Revenue sub-steps
            {
                selector: '[data-tour="revenue-time-selector"]',
                ...WIDGET_DESCRIPTIONS['revenue-time-selector'],
            },
            {
                selector: '[data-tour="revenue-actual"]',
                ...WIDGET_DESCRIPTIONS['revenue-actual'],
            },
            {
                selector: '[data-tour="revenue-target"]',
                ...WIDGET_DESCRIPTIONS['revenue-target'],
            },
            {
                selector: '[data-tour="revenue-completion"]',
                ...WIDGET_DESCRIPTIONS['revenue-completion'],
            },
            {
                selector: '[data-tour="widget-late_employees"]',
                ...WIDGET_DESCRIPTIONS['late-employees'],
            },
            // Late employees sub-steps
            {
                selector: '[data-tour="late-employees-stats"]',
                ...WIDGET_DESCRIPTIONS['late-employees-stats'],
            },
            {
                selector: '[data-tour="late-employees-calendar"]',
                ...WIDGET_DESCRIPTIONS['late-employees-calendar'],
            },
            {
                selector: '[data-tour="widget-events"]',
                ...WIDGET_DESCRIPTIONS.events,
            },
            {
                selector: '[data-tour="widget-daily-input"]',
                ...WIDGET_DESCRIPTIONS['daily-input'],
            },
            {
                selector: '[data-tour="widget-accounting-leads-statistics"]',
                ...WIDGET_DESCRIPTIONS['leads-statistics'],
            },
            // Leads sub-steps
            {
                selector: '[data-tour="leads-total"]',
                ...WIDGET_DESCRIPTIONS['leads-total'],
            },
            {
                selector: '[data-tour="leads-unverified"]',
                ...WIDGET_DESCRIPTIONS['leads-unverified'],
            },
            {
                selector: '[data-tour="leads-following"]',
                ...WIDGET_DESCRIPTIONS['leads-following'],
            },
            {
                selector: '[data-tour="widget-conversion-funnel"]',
                ...WIDGET_DESCRIPTIONS['conversion-funnel'],
            },
            // Funnel sub-steps
            {
                selector: '[data-tour="funnel-contacts"]',
                ...WIDGET_DESCRIPTIONS['funnel-contacts'],
            },
            {
                selector: '[data-tour="funnel-presentation"]',
                ...WIDGET_DESCRIPTIONS['funnel-presentation'],
            },
            {
                selector: '[data-tour="funnel-poc"]',
                ...WIDGET_DESCRIPTIONS['funnel-poc'],
            },
            {
                selector: '[data-tour="funnel-close-win"]',
                ...WIDGET_DESCRIPTIONS['funnel-close-win'],
            },
            {
                selector: '[data-tour="widget-invoice-waiting"]',
                ...WIDGET_DESCRIPTIONS['invoice-waiting'],
            },
            // Invoice sub-steps
            {
                selector: '[data-tour="invoice-tabs"]',
                ...WIDGET_DESCRIPTIONS['invoice-tabs'],
            },
            {
                selector: '[data-tour="invoice-waiting-summary"]',
                ...WIDGET_DESCRIPTIONS['invoice-waiting-summary'],
            },
            {
                selector: '[data-tour="widget-recently-paid"]',
                ...WIDGET_DESCRIPTIONS['recently-paid'],
            },
            {
                selector: '[data-tour="recently-paid-summary"]',
                ...WIDGET_DESCRIPTIONS['recently-paid-summary'],
            },
            {
                selector: '[data-tour="widget-invoice-revenue"]',
                ...WIDGET_DESCRIPTIONS['revenue-allocation'],
            },
            // Revenue allocation sub-steps
            {
                selector: '[data-tour="revenue-alloc-total"]',
                ...WIDGET_DESCRIPTIONS['revenue-alloc-total'],
            },
            {
                selector: '[data-tour="revenue-alloc-by-type"]',
                ...WIDGET_DESCRIPTIONS['revenue-alloc-by-type'],
            },
            {
                selector: '[data-tour="revenue-alloc-by-payment"]',
                ...WIDGET_DESCRIPTIONS['revenue-alloc-by-payment'],
            },
            {
                selector: '[data-tour="widget-service-trend"]',
                ...WIDGET_DESCRIPTIONS['service-trend'],
            },
            {
                selector: '[data-tour="service-trend-analysis"]',
                ...WIDGET_DESCRIPTIONS['service-trend-analysis'],
            },
            {
                selector: '[data-tour="widget-cancellation-rate"]',
                ...WIDGET_DESCRIPTIONS['cancellation-rate'],
            },
            // Cancellation rate sub-steps
            {
                selector: '[data-tour="cancellation-rate-avg"]',
                ...WIDGET_DESCRIPTIONS['cancellation-rate-avg'],
            },
            {
                selector: '[data-tour="cancellation-analysis"]',
                ...WIDGET_DESCRIPTIONS['cancellation-analysis'],
            },
            {
                selector: '[data-tour="widget-notification"]',
                ...WIDGET_DESCRIPTIONS.notification,
            },
            {
                selector: '[data-tour="widget-business"]',
                ...WIDGET_DESCRIPTIONS.business,
            },
            {
                selector: '[data-tour="widget-workday"]',
                ...WIDGET_DESCRIPTIONS.workday,
            },
        ],
    },
    'BOARD OF MANAGEMENT': {
        id: 'bom-dashboard',
        name: 'Dashboard Ban Quản Lý',
        description: 'Hướng dẫn các widget trên dashboard BQL',
        // Same as BOD
        steps: [
            {
                selector: '[data-tour="widget-quote_of_the_day"]',
                ...WIDGET_DESCRIPTIONS.quote_of_the_day,
            },
            {
                selector: '[data-tour="widget-revenue"]',
                ...WIDGET_DESCRIPTIONS.revenue,
            },
            {
                selector: '[data-tour="widget-late_employees"]',
                ...WIDGET_DESCRIPTIONS['late-employees'],
            },
            {
                selector: '[data-tour="widget-events"]',
                ...WIDGET_DESCRIPTIONS.events,
            },
            {
                selector: '[data-tour="widget-daily-input"]',
                ...WIDGET_DESCRIPTIONS['daily-input'],
            },
            {
                selector: '[data-tour="widget-accounting-leads-statistics"]',
                ...WIDGET_DESCRIPTIONS['leads-statistics'],
            },
            {
                selector: '[data-tour="widget-conversion-funnel"]',
                ...WIDGET_DESCRIPTIONS['conversion-funnel'],
            },
            {
                selector: '[data-tour="widget-invoice-waiting"]',
                ...WIDGET_DESCRIPTIONS['invoice-waiting'],
            },
            {
                selector: '[data-tour="widget-recently-paid"]',
                ...WIDGET_DESCRIPTIONS['recently-paid'],
            },
            {
                selector: '[data-tour="widget-invoice-revenue"]',
                ...WIDGET_DESCRIPTIONS['revenue-allocation'],
            },
            {
                selector: '[data-tour="widget-service-trend"]',
                ...WIDGET_DESCRIPTIONS['service-trend'],
            },
            {
                selector: '[data-tour="widget-cancellation-rate"]',
                ...WIDGET_DESCRIPTIONS['cancellation-rate'],
            },
            {
                selector: '[data-tour="widget-notification"]',
                ...WIDGET_DESCRIPTIONS.notification,
            },
            {
                selector: '[data-tour="widget-business"]',
                ...WIDGET_DESCRIPTIONS.business,
            },
            {
                selector: '[data-tour="widget-workday"]',
                ...WIDGET_DESCRIPTIONS.workday,
            },
        ],
    },
    // Default/General
    GENERAL: {
        id: 'general-dashboard',
        name: 'Dashboard Chung',
        description: 'Hướng dẫn các widget trên dashboard chung',
        steps: [
            {
                selector: '[data-tour="widget-quote_of_the_day"]',
                ...WIDGET_DESCRIPTIONS.quote_of_the_day,
            },
            {
                selector: '[data-tour="widget-notification"]',
                ...WIDGET_DESCRIPTIONS.notification,
            },
            {
                selector: '[data-tour="widget-workday"]',
                ...WIDGET_DESCRIPTIONS.workday,
            },
            {
                selector: '[data-tour="widget-my_workdays"]',
                ...WIDGET_DESCRIPTIONS.my_workdays,
            },
            {
                selector: '[data-tour="widget-events"]',
                ...WIDGET_DESCRIPTIONS.events,
            },
        ],
    },
}

interface UseDashboardTourOptions {
    department: string
}

// Alias mappings for departments that share same dashboard
const DEPARTMENT_ALIASES: Record<string, string> = {
    'SALE PARTNER': 'SALES',
    DEVELOPMENT: 'TECHNOLOGY',
}

export const useDashboardTour = ({ department }: UseDashboardTourOptions) => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    // Memoize tour config to prevent infinite re-renders
    const tourConfig = useMemo(() => {
        // Get tour config for department (handle aliases)
        const deptKey = department.toUpperCase()
        const aliasKey = DEPARTMENT_ALIASES[deptKey]
        let baseTourConfig =
            DEPARTMENT_TOURS[deptKey] || DEPARTMENT_TOURS.GENERAL

        // If steps array is empty (alias dept), use parent tour's steps
        if (baseTourConfig.steps.length === 0 && aliasKey) {
            baseTourConfig = {
                ...baseTourConfig,
                steps: DEPARTMENT_TOURS[aliasKey]?.steps || [],
            }
        }

        // Inject quote contribute step after quote_of_the_day
        const departmentStepsWithContribute = baseTourConfig.steps.flatMap(
            (step) =>
                step.selector.includes('quote_of_the_day')
                    ? [step, QUOTE_CONTRIBUTE_STEP]
                    : [step]
        )

        // Prepend intro steps and append final steps to all tours
        return {
            ...baseTourConfig,
            steps: [
                ...COMMON_INTRO_STEPS,
                ...departmentStepsWithContribute,
                ...COMMON_FINAL_STEPS,
            ],
        }
    }, [department])

    // Filter steps to only include visible widgets
    const getVisibleSteps = useCallback(() => {
        return tourConfig.steps.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [tourConfig])

    const showTourStep = useCallback((stepIndex: number, steps: TourStep[]) => {
        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        const step = steps[stepIndex]
        if (!step) return

        const element = document.querySelector(step.selector) as HTMLElement
        if (!element) {
            // Skip to next step if element not found
            if (stepIndex < steps.length - 1) {
                showTourStep(stepIndex + 1, steps)
            } else {
                endTour()
            }
            return
        }

        // Highlight element
        element.classList.add('tour-highlight-element')
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Show popover after scroll
        setTimeout(() => {
            const isLastStep = stepIndex === steps.length - 1
            const popover = createTourPopover({
                title: step.title,
                description: step.description,
                currentStep: stepIndex + 1,
                totalSteps: steps.length,
                onPrev:
                    stepIndex > 0
                        ? () => goToStep(stepIndex - 1, steps)
                        : undefined,
                onNext: isLastStep
                    ? endTour
                    : () => goToStep(stepIndex + 1, steps),
                onClose: endTour,
                isLastStep,
                demo: step.demo,
            })

            document.body.appendChild(popover)
            positionPopover(popover, element)
        }, 300)
    }, [])

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible widgets to tour')
            return
        }

        setIsActive(true)
        setCurrentStep(0)
        showTourStep(0, visibleSteps)
    }, [getVisibleSteps, showTourStep])

    return {
        isActive,
        currentStep,
        tourConfig,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useDashboardTour
